import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { listFolder, formatFileSize } from '@/services/filesApi';
import type { FileNode } from '@/types/files';

interface Props {
  isOpen: boolean;
  taskId?: string;
  onClose: () => void;
  onFilesCopied: (files: { id: string; name: string }[]) => Promise<void>;
}

const DiskFilePickerModal: React.FC<Props> = ({ isOpen, taskId: _taskId, onClose, onFilesCopied }) => {
  const { t } = useTranslation('tasks');
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [files, setFiles] = useState<FileNode[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<{ id: string | null; name: string }[]>([
    { id: null, name: t('diskPicker.root', 'Disk') },
  ]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const loadFolder = useCallback(async (folderId: string | null) => {
    setLoading(true);
    try {
      const children = await listFolder(folderId);
      setFiles(children || []);
    } catch (err) {
      console.error('Failed to load folder:', err);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentFolderId(null);
      setSelectedFiles(new Set());
      setSearch('');
      setError(null);
      setBreadcrumbs([{ id: null, name: t('diskPicker.root', 'Disk') }]);
    }
  }, [isOpen, t]);

  useEffect(() => {
    if (isOpen) {
      loadFolder(currentFolderId);
    }
  }, [isOpen, currentFolderId, loadFolder]);

  const navigateToFolder = (folder: FileNode) => {
    setCurrentFolderId(folder.id);
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
  };

  const navigateToBreadcrumb = (index: number) => {
    const bc = breadcrumbs[index];
    setCurrentFolderId(bc.id);
    setBreadcrumbs((prev) => prev.slice(0, index + 1));
  };

  const toggleFile = (fileId: string) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const handleConfirm = async () => {
    if (selectedFiles.size === 0) return;
    setCopying(true);
    setError(null);
    try {
      const selected = Array.from(selectedFiles).map(id => ({
        id,
        name: files.find(f => f.id === id)?.name || id,
      }));
      await onFilesCopied(selected);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to copy files');
    } finally {
      setCopying(false);
    }
  };


  const filteredFiles = search
    ? files.filter((f) => f.name.toLowerCase().includes(search.toLowerCase()))
    : files;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('diskPicker.title', 'Attach Files from Disk')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Breadcrumbs */}
        <div className="flex items-center gap-1 px-6 py-2 text-sm text-gray-500 bg-gray-50 border-b overflow-x-auto">
          {breadcrumbs.map((bc, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="text-gray-300">/</span>}
              <button
                onClick={() => navigateToBreadcrumb(i)}
                className="hover:text-blue-600 whitespace-nowrap"
              >
                {bc.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Search */}
        <div className="px-6 py-2 border-b">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('diskPicker.search', 'Search files...')}
            className="w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
          />
        </div>

        {/* File List */}
        <div className="flex-1 overflow-y-auto px-6 py-2 min-h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="text-center text-gray-400 py-12 text-sm">
              {search ? t('diskPicker.noResults', 'No files match your search') : t('diskPicker.empty', 'This folder is empty')}
            </div>
          ) : (
            <div className="space-y-0.5">
              {filteredFiles.map((file) => (
                <div
                  key={file.id}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    selectedFiles.has(file.id) ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    if (file.is_folder) {
                      navigateToFolder(file);
                    } else {
                      toggleFile(file.id);
                    }
                  }}
                >
                  {/* Checkbox for files */}
                  {!file.is_folder && (
                    <input
                      type="checkbox"
                      checked={selectedFiles.has(file.id)}
                      onChange={() => toggleFile(file.id)}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}

                  {/* Icon */}
                  {file.is_folder ? (
                    <svg className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M10 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2h-8l-2-2z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  )}

                  {/* Name */}
                  <span className="flex-1 text-sm truncate text-gray-800">{file.name}</span>

                  {/* Size */}
                  {!file.is_folder && file.file_size && (
                    <span className="text-xs text-gray-400 flex-shrink-0">{formatFileSize(file.file_size)}</span>
                  )}

                  {/* Folder arrow */}
                  {file.is_folder && (
                    <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-1.5 rounded">{error}</div>
          )}
          <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {selectedFiles.size > 0
              ? t('diskPicker.selectCount', '{{count}} file(s) selected', { count: selectedFiles.size })
              : t('diskPicker.noSelection', 'Select files to attach')}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              disabled={copying}
            >
              {t('diskPicker.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedFiles.size === 0 || copying}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {copying && (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              )}
              {t('diskPicker.select', 'Attach Selected')}
            </button>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiskFilePickerModal;
